/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ExportFeature.tsx
 * Project: mle-client-n
 * Committed last: 2022/11/22 @ 166
 * ---
 * Description:
 */

import {EditorFeature} from '../common/EditorFeature';
import {Editor} from '../../observables/Editor';
import {mleDomainGqlClient} from '../../../api/domain';
import {RESOLVE_DATAORIGINS} from '../../../api/domain/mutations.gql';
import Pamela from '@revodigital/pamela';
import {MpesInstanceManager} from '@revodigital/mpes';
import {jsPDF} from 'jspdf';
import {getOrientation, NameValue} from '../../../common/utils';
import {mapToObject} from '../../../common/array';
import {measureUnitToMeasureUnit, pixelToMeasureUnit} from '../../../common/measure-unit-utils';
import {MeasureUnits} from '../../../common/enums';
import {LABEL_PAPER} from "../work-area/DrawingAreaFeature";
import {isoSizesPixelDimensions} from "../../../common/project";

export const EXPORT_FNAME = 'ExportFeature';

export interface SeriesOptions {
    /**
     * The margin to apply to all the labels. It has to follow the measureUnit passed
     */
    labelMargin?: number;

    pageWidth: number;
    pageHeight: number;

    /**
     * Margin of the entire page. If not given is 0
     */
    pageMargin?: number;

    /**
     * Current measure unit of pageWidth and pageHeight
     */
    measureUnit: MeasureUnits;
    /**
     * Number of pages to fill. The default is 1
     */
    pages?: number;
}

export interface SeriesDetails {
    labelWidthMM: number;
    labelHeightMM: number;
    pageWidthMM: number;
    pageHeightMM: number;
    pageMarginMM: number;
    labelsPerRow: number;
    rowPerPage: number;
    labelsPerPage: number;
    pages: number;
    labelMarginMM: number;
}

export interface ExportToPdfOptions {
    fileName: string;
    variables: Map<string, string>;
    customPxRatio?: number;
}

/**
 * Funzionalità per gestire l'esportazione delle etichette
 */
export class ExportFeature extends EditorFeature<string> {
    constructor(editor: Editor) {
        super('ExportFeature', editor);
    }

    init(): boolean {
        return true;
    }

    /**
     * Esporta l'etichetta corrente in immagine
     * @param variables Valori delle variabili
     * @param ratio Pixel ratio
     */
    public async exportToImage(ratio: number, variables: Map<string, string>): Promise<string> {
        return this.getDataUrlOfDocument(await this.prepareForExport(variables),
            ratio, variables);
    }

    /**
     * Calcola il layout e tutte le informazioni riguardanti l'impaginazione del pdf
     * @param seriesOptions
     * @private
     */
    public calculatePdfSeriesLayout(seriesOptions: SeriesOptions): SeriesDetails {
        const widthMM = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelWidth);
        const heightMM = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelHeight);

        const pageWidthMM = measureUnitToMeasureUnit(seriesOptions.measureUnit, MeasureUnits.MM, seriesOptions.pageWidth);
        const pageHeightMM = measureUnitToMeasureUnit(seriesOptions.measureUnit, MeasureUnits.MM, seriesOptions.pageHeight);
        const margin = measureUnitToMeasureUnit(seriesOptions.measureUnit, MeasureUnits.MM, seriesOptions.labelMargin || 0);
        const pageMarginMM = measureUnitToMeasureUnit(seriesOptions.measureUnit, MeasureUnits.MM, seriesOptions.pageMargin || 0);

        let availableWidthMM = pageWidthMM;
        let availableHeightMM = pageHeightMM;

        // Applico il margine se specificato
        if (pageMarginMM > 0) {
            availableWidthMM -= pageMarginMM;
            availableHeightMM -= pageMarginMM;
        }

        const labelsPerRow = Math.floor(availableWidthMM / ((2 * margin) + widthMM));
        const numberOfRows = Math.floor(availableHeightMM / ((2 * margin) + heightMM));

        const totalLabelsPerPage = labelsPerRow * numberOfRows;
        const pages = seriesOptions.pages || 1;

        return {
            labelMarginMM: margin,
            pages,
            labelsPerRow,
            rowPerPage: numberOfRows,
            labelsPerPage: totalLabelsPerPage,
            pageHeightMM,
            pageWidthMM,
            labelHeightMM: heightMM,
            labelWidthMM: widthMM,
            pageMarginMM
        }
    }

    public checkSizeFits(iso: NameValue): boolean {
        const widthMM = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelWidth);
        const heightMM = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelHeight);

        const pageWidthMM = pixelToMeasureUnit(
            MeasureUnits.MM, isoSizesPixelDimensions[iso.name].width);
        const pageHeightMM = pixelToMeasureUnit(MeasureUnits.MM, isoSizesPixelDimensions[iso.name].height);

        return pageWidthMM >= widthMM && pageHeightMM >= heightMM;
    }

    public async generatePdfSeries(exportOptions: ExportToPdfOptions, seriesOptions: SeriesOptions): Promise<void> {
        const details = this.calculatePdfSeriesLayout(seriesOptions);
        // Get dataurl of resolved document
        const document = await this.prepareForExport(
            exportOptions.variables);
        const dataUrl = await this.getDataUrlOfDocument(document, exportOptions.customPxRatio || 3, exportOptions.variables);

        const pdf = new jsPDF({
            orientation:
                getOrientation(details.pageWidthMM,
                    details.pageHeightMM),
            unit:
                'mm',
            format:
                [details.pageWidthMM, details.pageHeightMM]
        });

        for (let p = 0; p < details.pages; p++) {
            // La prima pagina è creata in automatico
            if (p != 0) pdf.addPage([details.pageWidthMM, details.pageHeightMM], getOrientation(details.pageWidthMM,
                details.pageHeightMM))

            let curX = details.pageMarginMM;
            let curY = details.pageMarginMM;
            for (let yr = 0; yr < details.rowPerPage; yr++) {
                curX = details.pageMarginMM;
                for (let xr = 0; xr < details.labelsPerRow; xr++) {
                    pdf.addImage({
                        imageData: dataUrl,
                        x: curX,
                        y: curY,
                        width: details.labelWidthMM,
                        height: details.labelHeightMM
                    });
                    curX += details.labelMarginMM + details.labelWidthMM;
                }
                curY += details.labelHeightMM + details.labelMarginMM;
            }

            curX = details.pageMarginMM;
            curY = details.pageMarginMM;
        }

        pdf.save(exportOptions.fileName);
    }

    /**
     * Export this label into a pdf with one single page
     * @param fileName
     * @param variables Valori delle variabili
     */
    public async exportLabelToPdf(fileName: string, variables: Map<string, string>, customPxRatio?: number): Promise<void> {
        const autosave = this.sourcePointer.autoSave();
        autosave.suspendForTime(10000);

        const width = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelWidth);
        const height = pixelToMeasureUnit(MeasureUnits.MM,
            this.sourcePointer.projectData.labelHeight);

        // Get dataurl of resolved document
        const document = await this.prepareForExport(
            variables);
        let dataUrl = await this.getDataUrlOfDocument(document, customPxRatio != undefined ? customPxRatio : 3, variables);

        if (dataUrl == "data:,") {
            console.error("Impossibile generare il pdf. ")
            console.error("Verrà utilizzato il pixelratio 1");
            dataUrl = await this.getDataUrlOfDocument(document, 1, variables);
        }

        const pdf = new jsPDF({
            orientation:
                getOrientation(width,
                    height),
            unit:
                'mm',
            format:
                [width, height]
        });

        pdf.addImage(
            dataUrl,
            0,
            0,
            width,
            height
        );

        pdf.save(fileName);
    }

    destroy(): boolean {
        return true;
    }

    /**
     * Prepara l'etichetta per l'esportazione, risolvendo le origini dati e
     * fornendo il documento MPES con i dati effettivi
     * @private
     */
    public async prepareForExport(vars: Map<string, string>, overrideRecipe?: number): Promise<string> {
        const authFeature = this.sourcePointer.mylabelAuthFeature;

        // Login only if mylabel is connected
        if (authFeature.isConnected && authFeature.hasCredentials) {
            const res = await authFeature.performAuthOnService();
            if (!res) return '';
        }

        // Save manually
        await this.sourcePointer.saveFeature.encodeAndUpload();

        // Resolve dataorigins
        const resolved = await mleDomainGqlClient.mutate({
            mutation: RESOLVE_DATAORIGINS,
            variables: {
                id: this.sourcePointer.projectData.labelId,
                vars: JSON.stringify(mapToObject(vars)),
                activeRecipe: overrideRecipe || this.sourcePointer.recipeSwitcher.activeRevision,
                activeCompanyId: this.sourcePointer.recipeSwitcher.activeCompany
            },
        });

        return resolved.data.exportLabel;
    }

    public async exportToMPESDocument(): Promise<string> {
        const doc = await this.sourcePointer.saveFeature.encode({removeActiveStatus: true, updateSavingStatus: false});
        const buff = JSON.stringify(doc);
        return buff;
    }

    /**
     * Restituisce il dataurl di un documento mpes
     * @param document
     * @param pixelRatio
     * @param variables
     */
    private async getDataUrlOfDocument(document: string, pixelRatio: number = 1, variablesToAssign: Map<string, string>): Promise<string> {
        const hiddenSurface = this.sourcePointer.pamelaStage__nonObservable.clone() as Pamela.Stage;

        for (const l of hiddenSurface.getLayers()) {
            l.destroyChildren();
        }

        // Decode
        const decodedDoc = await MpesInstanceManager.getDecoderInstance().decodeBuffer(
            document);

        // Add into the stage
        hiddenSurface.addLayers(decodedDoc.content.data);

        // Rimuovo il bordo ma lascio lo sfondo
        const shape = hiddenSurface.getShapeWithName<Pamela.Rect>(LABEL_PAPER);

        if (shape) {
            shape.strokeWidth(0);
            shape.strokeEnabled(false);
        }

        hiddenSurface.width(decodedDoc.canvasprops.width);
        hiddenSurface.height(decodedDoc.canvasprops.height);
        hiddenSurface.scaleX(1);
        hiddenSurface.scaleY(1);
        hiddenSurface.absolutePosition({x: 0, y: 0});
        hiddenSurface.draw();

        // Risoluzione manuale variabili esportazione legacy
        if (variablesToAssign.size > 0)
            variablesToAssign.forEach((variableValue, variableName) => {
                hiddenSurface.getLayers().forEach(layer => {
                        const varToAssign = layer.find((variable: any) => (variable instanceof Pamela.ExportVariable) && variable.variableName() === variableName)[0] as Pamela.ExportVariable;
                        if (varToAssign)
                            varToAssign.assign(variablesToAssign.get(variableName) as string);
                    }
                );
            });

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(hiddenSurface.toDataURL({
                    pixelRatio,
                    quality: 1,
                    mimeType: 'image/jpeg',
                    height: hiddenSurface.height(),
                    width: hiddenSurface.width(),
                    x: 0,
                    y: 0
                }));
            }, 1200);
        });
    }
}